import { motion } from 'framer-motion';
import { Sparkles, Instagram, Facebook, Youtube, Twitter, Mail, Heart } from 'lucide-react';

interface FooterProps {
  onNavigate: (section: string) => void;
}

const Footer = ({ onNavigate }: FooterProps) => {
  const currentYear = new Date().getFullYear();

  const enlacesRapidos = [
    { label: 'Inicio', section: 'inicio' },
    { label: 'Cursos', section: 'cursos' },
    { label: 'Tienda', section: 'tienda' },
    { label: 'Blog', section: 'blog' },
    { label: 'Donaciones', section: 'donaciones' },
    { label: 'Contacto', section: 'contacto' },
  ];

  const servicios = [
    'Carta Astral',
    'Revolución Solar',
    'Sinastría de Pareja',
    'Tránsitos y Predicciones',
    'Astrología Infantil',
    'Elección de Fechas',
  ];

  const redesSociales = [
    { icono: Instagram, url: '#', nombre: 'Instagram' },
    { icono: Facebook, url: '#', nombre: 'Facebook' },
    { icono: Youtube, url: '#', nombre: 'YouTube' },
    { icono: Twitter, url: '#', nombre: 'Twitter' },
  ];

  return (
    <footer className="bg-purple-950/80 border-t border-purple-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-6 h-6 text-purple-300" />
              <span className="text-xl font-bold text-gradient">Trismosim</span>
            </div>
            <p className="text-purple-100/60 mb-6 text-sm leading-relaxed">
              Tu portal hacia el autoconocimiento a través de la astrología. 
              Descubre los secretos del cosmos y encuentra tu camino.
            </p>
            <div className="flex gap-3">
              {redesSociales.map((red, index) => (
                <motion.a
                  key={index}
                  href={red.url}
                  className="p-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 hover:text-purple-200 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title={red.nombre}
                >
                  <red.icono className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-purple-100 font-semibold mb-4">Enlaces Rápidos</h4>
            <ul className="space-y-2">
              {enlacesRapidos.map((enlace, index) => (
                <li key={index}>
                  <button
                    onClick={() => onNavigate(enlace.section)}
                    className="text-purple-100/60 hover:text-purple-300 transition-colors text-sm"
                  >
                    {enlace.label}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-purple-100 font-semibold mb-4">Servicios</h4>
            <ul className="space-y-2">
              {servicios.map((servicio, index) => (
                <li key={index}>
                  <span className="text-purple-100/60 text-sm">{servicio}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Newsletter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-purple-100 font-semibold mb-4">Newsletter</h4>
            <p className="text-purple-100/60 text-sm mb-4">
              Recibe predicciones mensuales y contenido exclusivo.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Tu email"
                className="flex-1 px-3 py-2 rounded-lg bg-purple-950/50 border border-purple-500/30 text-purple-100 placeholder:text-purple-400/50 text-sm focus:outline-none focus:border-purple-400"
              />
              <button className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm transition-colors">
                <Mail className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          className="mt-12 pt-8 border-t border-purple-500/20 flex flex-col md:flex-row items-center justify-between gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-purple-100/40 text-sm text-center md:text-left">
            © {currentYear} Lumina Astrología. Todos los derechos reservados.
          </p>
          <p className="text-purple-100/40 text-sm flex items-center gap-1">
            Hecho con <Heart className="w-4 h-4 text-purple-400" fill="currentColor" /> bajo las estrellas
          </p>
          <div className="flex gap-4 text-sm">
            <button className="text-purple-100/40 hover:text-purple-300 transition-colors">
              Privacidad
            </button>
            <button className="text-purple-100/40 hover:text-purple-300 transition-colors">
              Términos
            </button>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
